import turfArea from '@turf/area';
import turfBbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import getCirclePolygon from '@turf/circle';
import { cellToParent, polygonToCells } from 'h3-js';
import { last, uniq } from 'ramda';

import { setLayerVisibility } from '../pages/dashboards/common/helpers';
import { MAP_STYLES_MAP } from '../pages/dashboards/layerManagement/helpers';

export const POLYGON_GEOMETRY_TYPE = 'Polygon';
export const MULTIPOLYGON_GEOMETRY_TYPE = 'MultiPolygon';

const MIN_AREA = 300; // square km

const DEFAULT_COORDINATE = [24.9415, 60.1705]; // Helsinki

export const COUNTRY_CAPITAL_COORDINATE = {
  fi: DEFAULT_COORDINATE,
  dk: [12.56, 55.67], // Copenhagen
  se: [18.06861, 59.32944], // Stockholm
};

const MAPBOX_DEFAULT_HIDDEN_LAYERS = ['poi-label'];

const MAPBOX_DEFAULT_LABEL_LAYERS = [
  'road-label',
  'waterway-label',
  'water-line-label',
  'water-point-label',
  'airport-label',
  'settlement-subdivision-label',
  'settlement-label',
  'state-label',
  'country-label',
  'road-intersection',
  'road-number-shield',
  'road-exit-shield',
  'ferry-aerialway-label',
  'transit-label',
  'settlement-minor-label',
  'settlement-major-label',
  'continent-label',
];

const getStyleImage = (map, imageId) => {
  try {
    return map.style?.getImage?.(imageId) ?? null;
  } catch {
    return null;
  }
};

export const changeMapStyle = (mapStyleId, map) =>
  new Promise((resolve) => {
    if (map && MAP_STYLES_MAP[mapStyleId]) {
      const currentStyle = map.getStyle();
      const newStyleUrl = MAP_STYLES_MAP[mapStyleId].styleUrl;

      // When setting a new style, all custom images are removed. This hack
      // keeps them and adds them back as soon as the new style is loaded.
      const storedImages = {};

      map.listImages().forEach((imageId) => {
        if (imageId.startsWith('CHAOS_')) {
          const image = getStyleImage(map, imageId);
          if (image?.data) {
            storedImages[imageId] = image;
          }
        }
      });

      // style.load doesn't get called if we set the same style
      // which causes the map layers to disappear
      const currentStyleId = last(currentStyle.sprite.split('/'));
      const newStyleId = last(newStyleUrl.split('/'));

      if (currentStyleId !== newStyleId) {
        map.setStyle(newStyleUrl);

        map.once('style.load', () => {
          const newStyle = map.getStyle();

          // only keep custom layers
          const appLayers = currentStyle.layers.filter(
            (el) =>
              el.source && el.id !== 'satellite' && el.source !== 'composite',
          );

          newStyle.sources = {
            ...currentStyle.sources,
            ...newStyle.sources,
          };

          newStyle.layers = [...newStyle.layers, ...appLayers];

          map.setStyle(newStyle);

          // Add all custom images back to the map style
          Object.entries(storedImages).forEach(([id, image]) => {
            if (!map.hasImage(id)) {
              map.addImage(id, image.data, {
                sdf: image.sdf,
              });
            }
          });

          resolve();
        });
      } else {
        resolve();
      }
    } else {
      resolve();
    }
  });

const getDefaultCircleBbox = (center) => {
  const radius = Math.floor(Math.sqrt(MIN_AREA)) / 2;
  const newPolygon = getCirclePolygon(center, radius, 48, 'kilometers');
  return turfBbox(newPolygon);
};

export function getValidBbox(bbox, center) {
  const area = turfArea(bboxPolygon(bbox));
  const ValidAreaInSquareMetre = MIN_AREA * 1000000;

  if (ValidAreaInSquareMetre > area) {
    return bbox;
  }
  return getDefaultCircleBbox(center);
}

export const getMapBoundsFromBbox = (boundingBox) => {
  const west = boundingBox[0];
  const south = boundingBox[1];
  const east = boundingBox[2];
  const north = boundingBox[3];

  return [
    [west, south],
    [east, north],
  ];
};

export const getH3Cells = (
  polygon,
  targetResolution,
  searchResolution,
  isGeoJson,
) => {
  try {
    const geometryType = polygon?.geometry?.type;
    const geometryCoordinates = polygon?.geometry?.coordinates;

    if (!geometryType || !geometryCoordinates) return [];

    const validSearchResolution = Math.max(searchResolution, targetResolution);

    const getParentCells = (cells, resolution) => {
      if (resolution > targetResolution) {
        const parentCells = cells.map((cell) =>
          cellToParent(cell, targetResolution),
        );
        return getParentCells(parentCells, resolution - 1);
      }
      return cells;
    };

    const getCells = (coordinates, resolution) => {
      const cells = polygonToCells(coordinates, resolution, isGeoJson);
      if (cells.length <= 0 && resolution <= searchResolution) {
        return getCells(coordinates, resolution + 1);
      }

      return getParentCells(cells, resolution);
    };

    if (geometryType === MULTIPOLYGON_GEOMETRY_TYPE) {
      const multiPolygonCoordinates = geometryCoordinates;
      if (!Array.isArray(multiPolygonCoordinates)) return [];

      return uniq(
        multiPolygonCoordinates.flatMap((polygonCoordinates) =>
          getCells(polygonCoordinates[0], validSearchResolution),
        ),
      );
    }

    const polygonCoordinates = geometryCoordinates[0];

    if (!polygonCoordinates) return [];

    return uniq(getCells(polygonCoordinates, validSearchResolution));
  } catch (_e) {
    return [];
  }
};

export const setMapboxDefaultLayerVisibility = (
  map,
  showDefaultMapboxLabels,
) => {
  MAPBOX_DEFAULT_HIDDEN_LAYERS.map((layerName) =>
    setLayerVisibility(map, layerName, false),
  );
  MAPBOX_DEFAULT_LABEL_LAYERS.map((layerName) =>
    setLayerVisibility(map, layerName, showDefaultMapboxLabels),
  );
};
